import React, { useEffect, useState } from "react";
import { Spin, Table, Tag, Badge, Collapse } from "antd";
import { getFrontdeskDashboard } from "../../apis/roleDashboard";

interface BedAvailability {
  roomType: string;
  total: number;
  available: number;
  occupied: number;
}

interface RoomBed {
  roomId: number;
  roomName: string;
  roomType: string | null;
  status: string;
  beds: { id: number; name: string; isAvailable: boolean }[];
}

interface Discharge {
  id: number;
  patientName: string;
  room: string;
  admittedOn: string | null;
  days: number | null;
}

interface Appointment {
  id: number;
  patientName: string;
  phone: string | null;
  time: string | null;
  doctor: string | null;
  status: string | null;
  visitNumber: string | null;
}

interface FrontDeskData {
  bedAvailability: BedAvailability[];
  roomBeds: RoomBed[];
  expectedDischarges: Discharge[];
  appointmentsToday: Appointment[];
  activeIpdCount: number;
}

const FrontDeskDashboard: React.FC = () => {
  const [data, setData] = useState<FrontDeskData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFrontdeskDashboard()
      .then((res: any) => setData(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center mt-16"><Spin size="large" /></div>;
  if (!data) return <div className="text-gray-400 mt-8 text-center">Failed to load dashboard.</div>;

  const apptColumns = [
    { title: "Time", dataIndex: "time", key: "time", width: 70, render: (v: string) => v ?? "—" },
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v: string) => v ?? "—" },
    { title: "Doctor", dataIndex: "doctor", key: "doctor", render: (v: string) => v ?? "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => v ? <Tag>{v}</Tag> : null,
    },
  ];

  const dischargeColumns = [
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Room", dataIndex: "room", key: "room" },
    { title: "Admitted", dataIndex: "admittedOn", key: "admittedOn" },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
      render: (v: number) => <Badge count={v} color={v > 7 ? "orange" : "blue"} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Appointments Today</p>
          <p className="text-3xl font-bold text-blue-600">{data.appointmentsToday.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Active IPD</p>
          <p className="text-3xl font-bold text-purple-600">{data.activeIpdCount}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pending Discharges</p>
          <p className="text-3xl font-bold text-orange-500">{data.expectedDischarges.length}</p>
        </div>
      </div>

      {/* Bed availability by room type */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Bed Availability</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {data.bedAvailability.map((rt) => (
            <div
              key={rt.roomType}
              className={`rounded-lg p-3 border-2 text-center ${
                rt.available === 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"
              }`}
            >
              <p className="text-xs font-medium text-gray-600 truncate">{rt.roomType}</p>
              <p className="text-xl font-bold mt-1">
                <span className="text-green-600">{rt.available}</span>
                <span className="text-gray-400 text-sm"> / {rt.total}</span>
              </p>
              <p className="text-xs text-gray-500">free</p>
            </div>
          ))}
        </div>
      </div>

      {/* Room-level detail */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Room-level Bed Status</h3>
        <Collapse
          size="small"
          items={data.roomBeds.map((room) => ({
            key: room.roomId,
            label: (
              <span className="flex items-center gap-2">
                <span className="font-medium">{room.roomName}</span>
                <Tag color="default" className="text-xs">{room.roomType}</Tag>
                <Tag color={room.status === "available" ? "green" : "red"} className="text-xs">
                  {room.status}
                </Tag>
              </span>
            ),
            children: (
              <div className="flex flex-wrap gap-2">
                {room.beds.length === 0 ? (
                  <span className="text-gray-400 text-sm">No beds configured</span>
                ) : (
                  room.beds.map((bed) => (
                    <span
                      key={bed.id}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bed.isAvailable
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {bed.name ?? `Bed ${bed.id}`} {bed.isAvailable ? "✓" : "✗"}
                    </span>
                  ))
                )}
              </div>
            ),
          }))}
        />
      </div>

      {/* Today's appointments */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Today's Appointments</h3>
        <Table
          dataSource={data.appointmentsToday}
          columns={apptColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 15, hideOnSinglePage: true }}
          locale={{ emptyText: "No appointments today" }}
        />
      </div>

      {/* Expected discharges */}
      {data.expectedDischarges.length > 0 && (
        <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-200">
          <h3 className="font-semibold text-orange-700 mb-3">Pending Discharges</h3>
          <Table
            dataSource={data.expectedDischarges}
            columns={dischargeColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </div>
      )}
    </div>
  );
};

export default FrontDeskDashboard;
